// override https://github.com/shopware/frontends/blob/%40shopware-pwa/composables-next%401.1.1/packages/composables/src/useShopwareContext.ts

import { inject } from 'vue';
import type { ApiClient } from '#shopware';
import { useAuthCookie } from '~/composables/useAuthCookie';
import log from '~/utils/log';
import { COOKIE_NAME_SW_CONTEXT_TOKEN } from '~/types/cookieNames';

export type ShopwareContext = {
    devStorefrontUrl: string | null;
    /**
     * Shopware API client
     */
    apiClient: ApiClient;
    /**
     * Manually set the default headers for the Shopware API client based on URL path
     */
    setDefaultHeaders: (ctx: string) => void;
    /**
     * Route anonymous Store API reads through cacheable GET variants instead of POST
     * (introduced with @shopware/composables 1.12.0, read by its composables)
     */
    cacheableReads: boolean;
};

class ContextError extends Error {
    constructor(scope: string, message?: string) {
        super(`${scope} ${message ?? 'context is not provided'}`);
        this.name = 'ContextError';
    }
}

/**
 * @public
 * @category Context & Language
 */
export function useShopwareContext(): ShopwareContext {
    const shopwareContext = inject<ShopwareContext | null>('shopware', null);
    const injectedApiClient = inject<ApiClient | null>('apiClient', null);
    const { $shopwareApiClient } = useNuxtApp();
    const apiClient = injectedApiClient ?? $shopwareApiClient;

    if (!shopwareContext || !apiClient) {
        console.error('[Error][Shopware] API Client is not provided.');
        throw new ContextError('Shopware or apiClient');
    }

    const setDefaultHeaders = () => {
        const langId = useShopwareLanguageId();
        const swContextToken = useAuthCookie(COOKIE_NAME_SW_CONTEXT_TOKEN).value;

        if (!shopwareContext || !apiClient) {
            log.error('[Error][Shopware] API Client is not provided.');
            throw new ContextError('Shopware or apiClient');
        }

        apiClient.defaultHeaders.apply({
            'sw-language-id': langId,
            ...{ ...(swContextToken ? { 'sw-context-token': swContextToken } : {}) }
        });
    };

    setDefaultHeaders();

    return {
        setDefaultHeaders,
        apiClient,
        devStorefrontUrl: shopwareContext.devStorefrontUrl,
        cacheableReads: shopwareContext.cacheableReads ?? false
    };
}
